import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { useLang } from "../../contexts/LangContext";
import { useAuth } from "../../contexts/AuthContext";
import { LumfiWordmark } from "../../components/LumfiLogo";
import { tx } from "../../i18n";
import { Link } from "react-router-dom";
import { CapUsageBadge } from "../../components/CapUsageBadge";
import { useEffect } from "react";
import { trackDemoEvent } from "../../lib/demoEvents";
import {
  MessageSquare, Send, Phone, Sparkles, Coins, Calendar, CheckCircle2,
  AlertTriangle, Zap, Lock, BookOpen, ArrowRight, Bot, Camera, ListChecks, Receipt,
} from "lucide-react";

/**
 * Standalone reference for the Telegram & WhatsApp bots.
 * - What they do, what they don't
 * - Every command with sample input + sample output
 * - Quotas, limits, security caveats
 * - Quick troubleshooting
 *
 * iter-188 updates:
 *   • Proper PT-BR copy (no more "PT users see Spanish" shortcut).
 *   • New "Recibos por foto" section documenting the batch OCR flow
 *     that landed in iter-185+ (previously the doc still claimed
 *     "no file attachments").
 *   • Quick-jump table of contents at the top.
 */

const COMMANDS = [
  {
    cmd: "/start",
    en: "Welcome message + your personal 6-digit link code (Telegram only — WhatsApp picks up the code from the sandbox).",
    es: "Mensaje de bienvenida + tu código de 6 dígitos para enlazar (sólo Telegram — WhatsApp lo recibe vía el sandbox).",
  },
  {
    cmd: "/help",
    en: "Quick reference card — what the bot understands.",
    es: "Tarjeta de referencia — qué entiende el bot.",
  },
  {
    cmd: "/status",
    en: "Confirms your linked account.",
    es: "Confirma tu cuenta enlazada.",
  },
  {
    cmd: "/unlink",
    en: "Disconnects this chat from your Lumfi account.",
    es: "Desconecta este chat de tu cuenta de Lumfi.",
  },
];

const NL_EXAMPLES = [
  {
    type: "income",
    en: "Cliente Ana me pagó 800000",
    es: "Cliente Ana me pagó 800.000",
    saved_en: "Income $800,000 — Cliente Ana",
    saved_es: "Ingreso $800.000 — Cliente Ana",
  },
  {
    type: "expense",
    en: "Pagué 120k de internet",
    es: "Pagué 120k de internet",
    saved_en: "Expense $120,000 — internet",
    saved_es: "Gasto $120.000 — internet",
  },
  {
    type: "receivable",
    en: "Cliente Pedro me debe 500k viernes",
    es: "Cliente Pedro me debe 500k el viernes",
    saved_en: "Receivable $500,000 — Pedro — Fri",
    saved_es: "Por cobrar $500.000 — Pedro — viernes",
  },
  {
    type: "payment",
    en: "Pago de TC 5M el 16 de mayo",
    es: "Pago de TC 5M el 16 de mayo",
    saved_en: "Payment $5,000,000 — TC — May 16",
    saved_es: "Pago $5.000.000 — TC — 16 may",
  },
  {
    type: "recurring",
    en: "Renta 1.5M cada mes el día 5",
    es: "Renta 1.5M cada mes el día 5",
    saved_en: "Recurring payment Renta 1.5M monthly",
    saved_es: "Pago recurrente Renta 1.5M mensual",
  },
  {
    type: "mark_paid",
    en: "Ya pagué internet",
    es: "Ya pagué internet",
    saved_en: "Internet — marked paid (fuzzy match)",
    saved_es: "Internet — marcado pagado (match fuzzy)",
  },
  {
    type: "investment",
    en: "Invertí 1M en BTC al 15% anual, vence dic 2027",
    es: "Invertí 1M en BTC al 15% anual, vence dic 2027",
    saved_en: "Investment BTC $1M, rate 15%, exit Dec 2027",
    saved_es: "Inversión BTC $1M, tasa 15%, salida dic 2027",
  },
  {
    type: "debt",
    en: "TC Bancolombia saldo 3M al 28% APR, cuota 200k",
    es: "TC Bancolombia saldo 3M al 28% APR, cuota 200k",
    saved_en: "Debt TC Bancolombia, balance $3M, 28% APR",
    saved_es: "Deuda TC Bancolombia, saldo $3M, 28% APR",
  },
  {
    type: "goal",
    en: "Meta viaje a Europa 10M para diciembre",
    es: "Meta viaje a Europa 10M para diciembre",
    saved_en: "Goal Trip to Europe $10M target Dec",
    saved_es: "Meta Viaje a Europa $10M para dic",
  },
];

const SHORTCODE_HINTS = [
  { code: "P1, P2, …", en: "Mark scheduled payment #N as paid.", es: "Marca el pago programado #N como pagado." },
  { code: "R1, R2, …", en: "Mark receivable #N as collected.", es: "Marca el cobro #N como recibido." },
];

const BENEFITS = [
  {
    icon: Zap,
    en: "Capture on the go — register movements in seconds while the receipt is still in your hand.",
    es: "Captura sobre la marcha — registra movimientos en segundos mientras tienes el recibo en la mano.",
  },
  {
    icon: Sparkles,
    en: "Smart parsing — recognises currency, recurring frequency, payment vs receivable, and 8 entity types.",
    es: "Parser inteligente — reconoce moneda, frecuencia, pago vs por cobrar, y 8 tipos de entidades.",
  },
  {
    icon: Calendar,
    en: "Daily reminders — your bills + receivables for the next 3 days, with one-tap mark-paid via P1/R1 codes.",
    es: "Recordatorios diarios — tus pagos y cobros próximos 3 días, con marcar-pagado de un tap vía códigos P1/R1.",
  },
  {
    icon: Coins,
    en: "Multi-currency — say '100 USD' or '€50' and Lumfi snapshots the FX rate at capture.",
    es: "Multi-moneda — di '100 USD' o '€50' y Lumfi guarda la tasa FX en el momento.",
  },
  {
    icon: CheckCircle2,
    en: "Instant sync — what you type in Telegram lights up the open web tab in real time.",
    es: "Sync instantáneo — lo que escribes en Telegram refresca la pestaña web abierta en tiempo real.",
  },
];

const LIMITS = [
  {
    en: "Auto-save in bots: items the AI parses are saved immediately (no Approve step like the web chat).",
    es: "Auto-guardado en bots: los items que la IA detecta se guardan al instante (sin paso de Aprobar como en la web).",
    pt: "Salvar automático nos bots: os itens que a IA detecta são salvos instantaneamente (sem etapa de Aprovar como no chat web).",
  },
  {
    en: "One linked phone/Telegram per Lumfi account at a time.",
    es: "Un teléfono/Telegram enlazado por cuenta Lumfi a la vez.",
    pt: "Um telefone/Telegram vinculado por conta Lumfi de cada vez.",
  },
  {
    en: "Conversation memory is the last 10 messages per source (web vs Telegram vs WhatsApp).",
    es: "La memoria conversacional son los últimos 10 mensajes por canal (web vs Telegram vs WhatsApp).",
    pt: "A memória de conversa são as últimas 10 mensagens por canal (web vs Telegram vs WhatsApp).",
  },
  {
    en: "WhatsApp may use a verified sender — you must join with the keyword the first time. Production WhatsApp requires a paid Lumfi plan with a dedicated line.",
    es: "WhatsApp puede usar un remitente verificado — debes unirte con la palabra clave la primera vez. WhatsApp en producción requiere un plan Lumfi con línea dedicada.",
    pt: "O WhatsApp pode usar um remetente verificado — você precisa enviar a palavra-chave da primeira vez. WhatsApp em produção requer um plano pago Lumfi com linha dedicada.",
  },
  {
    en: "Photo OCR (Receipts AI) consumes a paid quota — each photo uses 1 credit, with a 3-extension cap per batch.",
    es: "El OCR por foto (Recibos IA) consume saldo pagado — cada foto usa 1 crédito, con un tope de 3 extensiones por lote.",
    pt: "O OCR por foto (Recibos IA) consome saldo pago — cada foto usa 1 crédito, com limite de 3 extensões por lote.",
  },
  {
    en: "Bots reply in the language saved on your profile, regardless of how you type.",
    es: "Los bots responden en el idioma guardado en tu perfil, sin importar cómo escribas.",
    pt: "Os bots respondem no idioma salvo no seu perfil, independente de como você escreva.",
  },
];

const SECURITY = [
  {
    en: "Linking is one-way: the bot must learn your 6-digit code; nobody else can hijack your data.",
    es: "El enlace es unidireccional: el bot debe aprender tu código de 6 dígitos; nadie más puede secuestrar tus datos.",
  },
  {
    en: "If you lose your phone, run /unlink from any client OR clear the linked phone on /integrations.",
    es: "Si pierdes el teléfono, ejecuta /unlink desde cualquier cliente O limpia el teléfono enlazado en /integrations.",
  },
  {
    en: "We never store your Telegram/WhatsApp chat outside the parsed financial summary you authorised.",
    es: "Nunca almacenamos tu chat de Telegram/WhatsApp fuera del resumen financiero parseado que tú autorizaste.",
  },
];

export default function BotGuide() {
  const { lang } = useLang();
  const { user } = useAuth();
  const isEs = lang === "es";

  // iter-205 — funnel: whatsapp_simulated (once per session).
  useEffect(() => {
    trackDemoEvent("whatsapp_simulated", {
      slug: user?.demo_slug,
      email: user?.email,
    }, { once: true });
  }, [user?.demo_slug, user?.email]);

  const SECTIONS = [
    { id: "quick-start", icon: Zap, label: tx(lang, "Quick start", "Inicio rápido", "Início rápido") },
    { id: "commands", icon: Bot, label: tx(lang, "Commands", "Comandos", "Comandos") },
    { id: "examples", icon: MessageSquare, label: tx(lang, "Examples", "Ejemplos", "Exemplos") },
    { id: "shortcodes", icon: Send, label: tx(lang, "Shortcuts", "Atajos", "Atalhos") },
    { id: "photos", icon: Camera, label: tx(lang, "Receipt photos", "Fotos de recibos", "Fotos de recibos") },
    { id: "benefits", icon: Sparkles, label: tx(lang, "Benefits", "Beneficios", "Benefícios") },
    { id: "limits", icon: AlertTriangle, label: tx(lang, "Limits", "Límites", "Limites") },
    { id: "security", icon: Lock, label: tx(lang, "Security", "Seguridad", "Segurança") },
  ];

  const pageBody = (
    <div className="max-w-4xl mx-auto space-y-6 fade-up" data-testid="bot-guide-page">
        <PageHeader
          testid="bot-guide-header"
          eyebrow={tx(lang, "Bots & channels", "Bots y canales", "Bots e canais")}
          title={tx(lang, "Bots guide — Telegram & WhatsApp",
            "Guía de bots — Telegram & WhatsApp",
            "Guia de bots — Telegram & WhatsApp")}
          subtitle={tx(lang,
            "Everything you can do from your messengers: commands, examples, shortcuts, benefits and limits.",
            "Todo lo que puedes hacer desde tus mensajeros: comandos, ejemplos, atajos, beneficios y límites.",
            "Tudo o que você pode fazer pelos seus mensageiros: comandos, exemplos, atalhos, benefícios e limites.")}
          actions={
            <Button asChild variant="outline" size="sm" className="gap-1" data-testid="bot-guide-go-integrations">
              <Link to="/integrations">
                <ArrowRight className="h-4 w-4" />
                {tx(lang, "Link bot", "Enlazar bot", "Vincular bot")}
              </Link>
            </Button>
          }
        />

        {/* iter-188 — Quick-jump TOC. Drives both the scroll-to-anchor
            navigation and serves as a visual table-of-contents for a
            page that was previously a long scroll with no landmarks. */}
        <nav
          data-testid="bot-guide-toc"
          className="flex flex-wrap gap-1.5 p-3 rounded-2xl border border-slate-200 bg-white/70 backdrop-blur-sm sticky top-2 z-10"
          aria-label={tx(lang, "Sections", "Secciones", "Seções")}
        >
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`bot-guide-toc-${s.id}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 hover:bg-sky-50 text-slate-700 hover:text-sky-700 text-[11px] font-medium transition-colors border border-transparent hover:border-sky-200"
              >
                <Icon className="h-3 w-3" />
                {s.label}
              </a>
            );
          })}
        </nav>

        {/* Quick start */}
        <Card id="quick-start" className="scroll-mt-20 p-6 rounded-2xl border-slate-200 bg-gradient-to-br from-sky-50 to-white">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-600" />
            {tx(lang, "Quick start", "Inicio rápido", "Início rápido")}
          </h2>
          <ol className="text-sm text-slate-700 space-y-2 list-decimal ml-5">
            <li>
              {tx(lang, "Visit ", "Visita ", "Visite ")}
              <Link to="/integrations" className="text-sky-700 underline">/integrations</Link>
              {tx(lang, " and copy your 6-digit code.", " y copia tu código de 6 dígitos.", " e copie seu código de 6 dígitos.")}
            </li>
            <li>
              <strong>Telegram:</strong>{" "}
              {tx(lang,
                "open @LumfiAppBot, send /start, then paste the code.",
                "abre @LumfiAppBot, manda /start, luego pega el código.",
                "abra @LumfiAppBot, envie /start, depois cole o código.")}
              <br />
              <strong>WhatsApp:</strong>{" "}
              {tx(lang,
                "send join <keyword> to the WhatsApp sandbox number (instructions appear when you request the code), then send the code.",
                "envía join <keyword> al número del sandbox de WhatsApp (las instrucciones aparecen al pedir el código), luego envía el código.",
                "envie join <keyword> ao número do sandbox do WhatsApp (instruções aparecem ao pedir o código), depois envie o código.")}
            </li>
            <li>
              {tx(lang,
                "Start typing movements in natural language — examples below.",
                "Empieza a escribir movimientos en lenguaje natural — los ejemplos están abajo.",
                "Comece a digitar movimentos em linguagem natural — exemplos abaixo.")}
            </li>
          </ol>
        </Card>

        {/* Commands */}
        <Card id="commands" className="scroll-mt-20 p-6 rounded-2xl border-slate-200" data-testid="bot-guide-commands">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Bot className="h-4 w-4 text-sky-600" />
            {tx(lang, "Commands", "Comandos", "Comandos")}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-separate border-spacing-y-1">
              <thead className="text-xs uppercase text-slate-500 tracking-wide">
                <tr>
                  <th className="text-left py-2 px-3 w-28">{tx(lang, "Command", "Comando", "Comando")}</th>
                  <th className="text-left py-2 px-3">{tx(lang, "What it does", "Qué hace", "O que faz")}</th>
                </tr>
              </thead>
              <tbody>
                {COMMANDS.map((c) => (
                  <tr key={c.cmd} className="bg-slate-50/50">
                    <td className="py-2 px-3 font-mono text-xs text-slate-900">{c.cmd}</td>
                    <td className="py-2 px-3 text-slate-700">{isEs ? c.es : c.en}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            {tx(lang,
              "Any other message is interpreted as a natural-language movement.",
              "Cualquier otro mensaje se interpreta como un movimiento en lenguaje natural.",
              "Qualquer outra mensagem é interpretada como um movimento em linguagem natural.")}
          </p>
        </Card>

        {/* Natural-language examples */}
        <Card id="examples" className="scroll-mt-20 p-6 rounded-2xl border-slate-200" data-testid="bot-guide-examples">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-emerald-600" />
            {tx(lang, "Examples by movement type", "Ejemplos por tipo de movimiento", "Exemplos por tipo de movimento")}
          </h2>
          <div className="space-y-2">
            {NL_EXAMPLES.map((ex) => (
              <div
                key={ex.type}
                data-testid={`bot-guide-example-${ex.type}`}
                className="grid grid-cols-1 sm:grid-cols-[100px,1fr,1fr] gap-2 sm:gap-3 items-start text-sm py-2 border-b last:border-b-0 border-slate-100"
              >
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-bold border-sky-200 text-sky-700 bg-sky-50 w-fit">
                  {ex.type}
                </Badge>
                <div className="font-mono text-xs text-slate-900 bg-slate-50 rounded px-2 py-1.5 border border-slate-100">
                  {isEs ? ex.es : ex.en}
                </div>
                <div className="text-xs text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 shrink-0" />
                  <span>{isEs ? ex.saved_es : ex.saved_en}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Short-code shortcuts */}
        <Card id="shortcodes" className="scroll-mt-20 p-6 rounded-2xl border-slate-200" data-testid="bot-guide-shortcodes">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Send className="h-4 w-4 text-violet-600" />
            {tx(lang, "Short-code shortcuts (P1/R1)", "Atajos de respuesta rápida (P1/R1)", "Atalhos de resposta rápida (P1/R1)")}
          </h2>
          <p className="text-sm text-slate-600 mb-3">
            {tx(lang,
              "Every daily reminder enumerates your upcoming payments & receivables. To mark one paid/collected without retyping its name, reply with the code:",
              "Cada recordatorio diario enumera tus pagos y cobros próximos. Para marcar uno como pagado/recibido sin escribir el nombre completo, responde el código:",
              "Cada lembrete diário enumera seus pagamentos e cobranças próximas. Para marcar um como pago/recebido sem digitar o nome todo, responda com o código:")}
          </p>
          <div className="space-y-2">
            {SHORTCODE_HINTS.map((s) => (
              <div key={s.code} className="flex items-start gap-3 text-sm">
                <code className="bg-violet-100 text-violet-700 font-bold rounded px-2 py-0.5 text-xs">
                  {s.code}
                </code>
                <span className="text-slate-700">{isEs ? s.es : s.en}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* iter-188 — Receipt photos (OCR) — previously missing.
            Documents the batch flow that landed in iter-185. */}
        <Card id="photos" className="scroll-mt-20 p-6 rounded-2xl border-amber-200 bg-amber-50/40" data-testid="bot-guide-photos">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Camera className="h-4 w-4 text-amber-600" />
            {tx(lang, "Receipt photos (OCR)", "Fotos de recibos (OCR)", "Fotos de recibos (OCR)")}
            <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-100 text-amber-800">
              {tx(lang, "Paid feature", "Feature pago", "Feature paga")}
            </Badge>
            <CapUsageBadge resourceKey="image_extractions_monthly" isEs={lang === "es"} />
          </h2>
          <p className="text-sm text-slate-700 leading-snug mb-3">
            {tx(lang,
              "Send a photo (PNG/JPG) or a PDF straight from your phone. Lumfi extracts items, amounts and currencies, shows you a preview and only saves after you confirm.",
              "Manda una foto (PNG/JPG) o un PDF directo desde tu teléfono. Lumfi extrae items, montos y monedas, te muestra una vista previa y guarda sólo después de tu confirmación.",
              "Envie uma foto (PNG/JPG) ou um PDF direto do seu telefone. Lumfi extrai itens, valores e moedas, mostra uma pré-visualização e salva apenas depois da sua confirmação.")}
          </p>
          <div className="space-y-2 text-sm text-slate-700">
            <div className="flex items-start gap-2">
              <Receipt className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <span>{tx(lang,
                "Each photo consumes 1 credit from your Receipts AI quota.",
                "Cada foto consume 1 crédito de tu cuota de Recibos IA.",
                "Cada foto consome 1 crédito da sua cota de Recibos IA.")}</span>
            </div>
            <div className="flex items-start gap-2">
              <ListChecks className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <span>{tx(lang,
                "Batch up to ~10 photos per minute — keep them coming and Lumfi will OCR them in one shot.",
                "Mandá hasta ~10 fotos por minuto en un mismo lote — Lumfi las procesa todas en una sola pasada.",
                "Envie até ~10 fotos por minuto no mesmo lote — Lumfi processa tudo de uma vez.")}</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <span>{tx(lang,
                "From the preview you can: type \"save\" / \"yes\" to confirm, \"change N field value\" to fix an item, \"retry N\" to re-OCR a bad photo, or \"remove N\" / /cancel to drop the batch.",
                "Desde la vista previa puedes: escribir \"guardar\" / \"sí\" para confirmar, \"cambiar N campo valor\" para arreglar un item, \"reintentar N\" para reprocesar una foto borrosa, o \"quitar N\" / /cancelar para descartar el lote.",
                "Da pré-visualização você pode: digitar \"salvar\" / \"sim\" para confirmar, \"mudar N campo valor\" para corrigir um item, \"repetir N\" para reprocessar uma foto ruim, ou \"remover N\" / /cancelar para descartar.")}</span>
            </div>
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <span>{tx(lang,
                "If you cancel before saving, the credit is refunded automatically.",
                "Si cancelas antes de guardar, el crédito se devuelve automáticamente.",
                "Se você cancelar antes de salvar, o crédito é devolvido automaticamente.")}</span>
            </div>
          </div>
        </Card>

        {/* Benefits */}
        <Card id="benefits" className="scroll-mt-20 p-6 rounded-2xl border-slate-200">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            {tx(lang, "Benefits", "Beneficios", "Benefícios")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.en} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50/60 border border-slate-100">
                  <Icon className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-700">{isEs ? b.es : b.en}</p>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Limits */}
        <Card id="limits" className="scroll-mt-20 p-6 rounded-2xl border-amber-200 bg-amber-50/30" data-testid="bot-guide-limits">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            {tx(lang, "Limits & gotchas", "Límites & cosas a saber", "Limites & detalhes")}
          </h2>
          <ul className="text-sm text-slate-700 space-y-2 list-disc ml-5">
            {LIMITS.map((l) => (
              <li key={l.en}>{tx(lang, l.en, l.es, l.pt || l.es)}</li>
            ))}
          </ul>
        </Card>

        {/* Security */}
        <Card id="security" className="scroll-mt-20 p-6 rounded-2xl border-slate-200" data-testid="bot-guide-security">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Lock className="h-4 w-4 text-slate-600" />
            {tx(lang, "Security", "Seguridad", "Segurança")}
          </h2>
          <ul className="text-sm text-slate-700 space-y-2 list-disc ml-5">
            {SECURITY.map((s) => (
              <li key={s.en}>{isEs ? s.es : s.en}</li>
            ))}
          </ul>
        </Card>

        {/* Troubleshooting */}
        <Card className="p-6 rounded-2xl border-slate-200">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Phone className="h-4 w-4 text-red-500" />
            {tx(lang, "Troubleshooting", "Problemas frecuentes", "Problemas frequentes")}
          </h2>
          <div className="space-y-3 text-sm">
            <div>
              <strong className="text-slate-900">
                {tx(lang, "“The bot doesn't reply”", "“El bot no responde”", "“O bot não responde”")}
              </strong>
              <p className="text-slate-600 mt-1">
                {tx(lang,
                  "Make sure you sent /start (Telegram) or the sandbox join keyword (WhatsApp). Verify the link on /integrations.",
                  "Asegúrate de haber enviado /start (Telegram) o el join keyword del sandbox (WhatsApp). Verifica el enlace en /integrations.",
                  "Confirme que enviou /start (Telegram) ou a join keyword do sandbox (WhatsApp). Verifique o vínculo em /integrations.")}
              </p>
            </div>
            <div>
              <strong className="text-slate-900">
                {tx(lang, "“It didn't save the details”", "“No guardó los detalles”", "“Não salvou os detalhes”")}
              </strong>
              <p className="text-slate-600 mt-1">
                {tx(lang,
                  "Lumfi always keeps your verbatim message in the notes field. Open the item on /investments, /debts, /goals, /receivables or /payments to see it.",
                  "Lumfi siempre conserva tu mensaje original en el campo notas. Abre el item en /investments, /debts, /goals, /receivables o /payments para verlo completo.",
                  "Lumfi sempre guarda sua mensagem original no campo notas. Abra o item em /investments, /debts, /goals, /receivables ou /payments para ver tudo.")}
              </p>
            </div>
            <div>
              <strong className="text-slate-900">
                {tx(lang, "“I want to switch the bot's language”", "“Quiero cambiar el idioma del bot”", "“Quero trocar o idioma do bot”")}
              </strong>
              <p className="text-slate-600 mt-1">
                {tx(lang, "Toggle the language on your ", "Cambia el idioma en tu ", "Mude o idioma no seu ")}
                <Link to="/profile" className="text-sky-700 underline">{tx(lang, "profile", "perfil", "perfil")}</Link>
                {tx(lang, " — affects web AND bot.", " — afecta tanto la web como el bot.", " — afeta a web E o bot.")}
              </p>
            </div>
          </div>
        </Card>
      </div>
  );

  // iter-203 — anonymous visitors (linked from sitemap / marketing
  // CTA) get a minimal public shell instead of <Layout>, which
  // assumes a signed-in user + profile + sidebar and would 401-spam.
  if (!user) {
    return (
      <div className="min-h-screen bg-cream-100" data-testid="bot-guide-public">
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-cream-100/80 border-b border-cream-300/70">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="bot-guide-home-link">
              <LumfiWordmark height={28} />
            </Link>
            <div className="flex items-center gap-2">
              <Button asChild variant="ghost" size="sm" data-testid="bot-guide-login-cta">
                <Link to="/login">{tx(lang, "Sign in", "Iniciar sesión", "Entrar")}</Link>
              </Button>
              <Button asChild size="sm" className="rounded-full" data-testid="bot-guide-signup-cta">
                <Link to="/register">{tx(lang, "Start free", "Empezar gratis", "Começar grátis")}</Link>
              </Button>
            </div>
          </div>
        </header>
        <main className="px-4 py-6">
          {pageBody}
        </main>
        <footer className="border-t border-cream-300 py-6 mt-10 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Lumfi · {tx(lang, "Public reference", "Referencia pública", "Referência pública")}
        </footer>
      </div>
    );
  }

  return (
    <Layout>
      {pageBody}
    </Layout>
  );
}
